import { AlertCircle, Bell, Calendar, Loader2, Users } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useNotificationPermissionStore } from "@/lib/push-notifications/store/notification-permission-store";

interface SimpleNotificationPermissionDialogProps {
  open: boolean;
  onClose: () => void;
}

export function SimpleNotificationPermissionDialog({
  open,
  onClose,
}: SimpleNotificationPermissionDialogProps) {
  const { t } = useTranslation("notifications");
  const [isRequesting, setIsRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { requestPermission } = useNotificationPermissionStore();

  const handleEnable = async () => {
    setIsRequesting(true);
    setError(null);

    try {
      const success = await requestPermission();

      if (success) {
        onClose();
      } else {
        setError(
          t(
            "permission.denied",
            "Permission was denied. You can enable notifications later in your settings."
          )
        );
      }
    } catch (_err) {
      setError(t("permission.requestFailed", "Failed to request permission. Please try again."));
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDontAskAgain = () => {
    localStorage.setItem("notification-permission-permanently-dismissed", "true");
    localStorage.setItem("notification-permission-permanently-dismissed-at", Date.now().toString());
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] [&_button[aria-label='Close']]:hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-blue-600" />
            {t("permission.title", "Stay Updated with Your Salon")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "permission.description",
              "Enable push notifications to receive important updates about your appointments and business."
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Benefits */}
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium">
                  {t("permission.benefits.appointments.title", "Appointment Reminders")}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {t(
                    "permission.benefits.appointments.description",
                    "Get notified about upcoming appointments and schedule changes"
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium">
                  {t("permission.benefits.urgent.title", "Urgent Updates")}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {t(
                    "permission.benefits.urgent.description",
                    "Receive immediate alerts for important business updates"
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Users className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium">
                  {t("permission.benefits.team.title", "Team Communications")}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {t(
                    "permission.benefits.team.description",
                    "Stay connected with staff notifications and updates"
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Error display */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <div className="flex flex-col sm:flex-row gap-2 w-full">
            <div className="flex gap-2 sm:order-2">
              <Button
                onClick={handleEnable}
                disabled={isRequesting}
                className="flex-1 sm:flex-none"
              >
                {isRequesting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t("permission.actions.requesting", "Requesting...")}
                  </>
                ) : (
                  <>
                    <Bell className="h-4 w-4 mr-2" />
                    {t("permission.actions.enable", "Enable Notifications")}
                  </>
                )}
              </Button>
            </div>

            <div className="flex justify-start sm:order-1 sm:flex-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDontAskAgain}
                disabled={isRequesting}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                {t("permission.actions.dontAskAgain", "Don't ask again")}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
